<template>
	<div class="sr" :class="{ 'full-width': fullWidth }">
		<div
			class="sr__container pt-0 radius-xsmall border-100 border-high-active bg-low-active"
			:class="{
				'full-width': fullWidth,
				'sr__container--no-click': isLandingPageEmpty,
			}"
		>
			<div
				class="sr__overlay"
				:class="{
					'sr__overlay--gradient': !sponsoredAd?.campaign?.promotionalImageUrl,
					'sr__overlay--dark-top': isApplyTitleGradient,
				}"
			>
				<div class="sr__content-container" :class="{ 'full-width': fullWidth }">
					<div class="sr__content g-4">
						<slot name="provider-icon"> </slot>

						<div class="sr__title-container gc-3">
							<h2
								class="sr__title m-0 g-2"
								:class="isHighContrast ? 'text-medium-upsidedown' : 'text-high'"
							>
								<a :href="landingPageURL" target="_blank" rel="noopener" @click="onSRListClick">
									{{ promotionalTitle }}
								</a>
							</h2>

							<ListToggle
								:isFollowed="!!campaignNode?.followedlistEntry"
								:listId="campaignNode?.id"
								:listName="campaignNode?.content.name"
								@click="onFollowClicked"
							/>
						</div>
					</div>

					<div
						class="sr__list-container"
						:class="{
							'full-width': fullWidth,
							'has-disclaimer': sponsoredAd?.campaign?.disclaimerText != null,
						}"
					>
						<SRTitleListGraphql
							v-if="titles && titles.length > 0"
							:title="null"
							:titles="titles"
							impressionTrackingLabel="titlelist"
							impressionTrackingProperty="sr_titlelist"
							:additionalContexts="additionalContexts"
							:sponsoredAd="sponsoredAd"
							:showSponsoredAd="false"
							:fullWidth="fullWidth"
							:placement="placement"
							@onPosterClick="onSRListPosterClicked"
							@onPosterClickOut="onSRListPosterClickedOut"
							@imageError="src => $emit('imageError', src)"
						/>
						<div
							v-if="sponsoredAd?.campaign?.disclaimerText != null"
							class="sr__disclaimer text-muted"
							:class="{ 'full-width': fullWidth, 'bigger-font': isBiggerFontCampaign }"
						>
							{{ sponsoredAd?.campaign?.disclaimerText }}
						</div>
					</div>
				</div>
			</div>

			<div v-if="sponsoredAd?.campaign?.promotionalImageUrl" class="sr__image-container">
				<picture>
					<source
						type="image/avif"
						:srcset="sponsoredAd?.campaign?.promotionalImageUrl?.replace(/\.\w+$/, '.avif')"
					/>

					<img
						class="sr__img"
						:loading="lazy ? 'lazy' : 'eager'"
						:src="sponsoredAd?.campaign?.promotionalImageUrl"
						:alt="campaignNode?.content.name"
						@error="$emit('imageError', sponsoredAd?.campaign?.promotionalImageUrl)"
					/>
				</picture>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import ListToggle from '@/features/sponsored-recommendations/components/ListToggle.vue';

import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';

import { SraEventAction } from '@/@types/graphql-types';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import type { SRClickTitleEvent, ListSR, SRClickEvent, ListTitleSR } from '@/features/sponsored-recommendations/types';
import SRTitleListGraphql from '@/components/SRTitleListGraphql.vue';
import { useBackendTracking } from '@/helpers/composables/useBackendTracking';
import type { Maybe } from '@/helpers/types';
import { ClickoutPlacement } from '@/interfaces/snowplow/clickout-context';

const props = withDefaults(
	defineProps<{
		sponsoredAd: SponsoredAdFragment & {
			campaign: NonNullable<SponsoredAdFragment['campaign'] & { node: ListSR }>;
		};
		lazy?: boolean;
		fullWidth?: boolean;
		additionalContexts?: SnowplowContext[];
		placement?: ClickoutPlacement;
	}>(),
	{
		lazy: true,
		fullWidth: false,
		additionalContexts: () => [],
	}
);

const emits = defineEmits<{
	(e: 'onPosterClick', data: SRClickTitleEvent<ListTitleSR>): void;
	(e: 'onTrackerClickout', data?: SRClickEvent<ListTitleSR>): void;
	(e: 'imageError', src: Maybe<string>): void;
}>();

const { trackSraEvent } = useBackendTracking();

const sponsoredCampaign = computed(() => props.sponsoredAd?.campaign);
const campaignNode = computed(() => sponsoredCampaign.value?.node);

// Template Variables
const promotionalTitle = computed(() => sponsoredCampaign.value?.promotionalTitle || campaignNode.value?.content.name);

const titles = computed(() => campaignNode.value?.titles.edges?.map(edge => edge.node) ?? []);

const landingPageURL = computed(() => sponsoredCampaign.value?.watchNowOffer.standardWebURL ?? '');

const isLandingPageEmpty = computed(() => !landingPageURL.value.trim().length);

function onSRListPosterClicked({ title, position }: SRClickEvent<ListTitleSR>) {
	// pass down click event after title got clicked
	emits('onPosterClick', { title, position });
}

function onSRListPosterClickedOut({ title, position }: SRClickEvent<ListTitleSR>) {
	trackSraEvent({
		action: SraEventAction.Clickout,
		sponsoredAd: () => props.sponsoredAd,
		jwEntityID: title.id,
		position,
	});
	emits('onTrackerClickout', { title, position });
}

function onSRListClick() {
	trackSraEvent({
		action: SraEventAction.Click,
		sponsoredAd: () => props.sponsoredAd,
	});

	if (landingPageURL.value) {
		trackSraEvent({
			action: SraEventAction.ListClickout,
			sponsoredAd: () => props.sponsoredAd,
		});
	}
	emits('onTrackerClickout', { title: titles.value[0], position: 0 });
}

function onFollowClicked() {
	trackSraEvent({
		action: SraEventAction.FollowClicked,
		sponsoredAd: () => props.sponsoredAd,
	});
}

// Hacks & Exceptions
/** Campaigns with a light colored background that need darker text. */
const highContrastCampaigns = ['TWIXMAS_BURST1_GB_SRA'];
const isHighContrast = computed(() => highContrastCampaigns.includes(props.sponsoredAd.campaign.name));
const biggerFontCampaigns = ['BRITBOX_MULTI-TITLE_BURST1_AU_SRA'];
/** Apply gradient to the title on light backgrounds */
const applyTitleGradientImageAsset = ['WB_Campaign2025_Autumn'];
const isApplyTitleGradient = computed(() =>
	applyTitleGradientImageAsset.some(asset => props.sponsoredAd.campaign.promotionalImageUrl?.includes(asset))
);
/** Campaigns that need a bigger font for the disclaimer text */
const isBiggerFontCampaign = computed(() => biggerFontCampaigns.includes(props.sponsoredAd.campaign.name));
</script>

<style lang="scss" scoped>
.sr {
	position: relative;
	height: 100%;

	&.full-width {
		height: 420px;

		@container (max-width : 500px) {
			height: 400px;
		}
	}

	&__container {
		position: relative;
		display: flex;
		overflow: hidden;
		transition: opacity 0.1s ease;
		width: 100%;
		height: 100%;
		will-change: opacity;

		&.full-width {
			border: none;
		}

		&:active:not(&--no-click) {
			opacity: 0.5;
		}
	}

	&__overlay {
		display: flex;
		flex-direction: column;
		position: absolute;
		z-index: 1;
		top: 0;
		width: 100%;
		height: 100%;

		&--gradient {
			background: linear-gradient(180deg, rgba(19, 27, 32, 0.48) 0%, #141c22 100%),
				linear-gradient(90deg, #0d186d 18.7%, rgba(16, 22, 29, 0.48) 94.67%);
		}

		&--dark-top {
			background: linear-gradient(180deg, rgba(0, 0, 0, 0.8) 0%, transparent 35%);
		}
	}

	&__image-container,
	&__img {
		height: 100%;
		width: 100%;
	}

	&__img {
		display: inline-block;
		width: 100%;
		height: 100%;
		object-fit: cover;
		position: absolute;
		top: 0;
		left: 0;
	}

	&__content {
		display: flex;
		align-items: center;
	}

	&__content-container {
		padding-inline-start: var(--jw-spacing-4); // 16px;
		padding-top: var(--jw-spacing-2); // 10px;

		&.full-width {
			display: flex;
			flex-direction: column;
			position: relative;
			height: 100%;
			justify-content: center;
			padding-inline-start: var(--jw-spacing-12); // 48px;
			padding-top: var(--jw-spacing-0); // 0

			@container (max-width : 800px) {
				padding-inline-start: var(--jw-spacing-6); // 24px;
			}
		}

		:deep(.provider-icon) {
			flex-shrink: 0;
		}
	}

	&__list-container {
		margin-top: var(--jw-spacing-3);
		margin-bottom: calc(var(--jw-spacing-12) * -1);

		&.full-width {
			margin-bottom: var(--jw-spacing-0);
			margin-top: var(--jw-spacing-6);
		}
	}

	&__title-container {
		flex-direction: row;
		display: flex;

		@container (max-width : 800px) {
			flex-direction: column;
		}
	}

	&__title {
		/* heading/heading-high-small */
		font-size: 22px;
		font-style: normal;
		font-weight: 900;
		line-height: 32px; /* 145.455% */
		letter-spacing: 0.22px;
		white-space: pre-line;

		word-break: break-word;
		overflow-wrap: break-word;

		@container (max-width : 800px) {
			font-size: 20px;
			line-height: 28px;
			font-weight: 900;
			letter-spacing: 0.2px;
		}

		@container (max-width : 500px) {
			font-size: 18px;
			line-height: 24px;
			font-weight: 900;
			letter-spacing: 0.18px;
		}

		@container (max-width : 400px) {
			font-size: 16px;
			line-height: 22px;
			font-weight: 900;
			letter-spacing: 0.18px;
		}

		a {
			color: inherit;

			&::after {
				content: '';
				position: absolute;
				inset: 0;
			}
		}

		&.text-medium-upsidedown + div.list-toggle {
			color: var(--jw-grey-400);
		}
	}

	// If we have a disclaimer, adjust the bottom margin & make posters smaller
	&__list-container:not(.full-width).has-disclaimer {
		:deep(.title-list) {
			margin-bottom: 12px;
			.horizontal-title-list .title-poster {
				width: 170px;
			}
		}
	}

	&__disclaimer {
		font-size: 10px;

		&.full-width {
			font-size: 12px;
			margin-top: var(--jw-spacing-4);

			&.bigger-font {
				font-size: 14px;
			}

			@container (max-width : 800px) {
				margin-top: var(--jw-spacing-3);
			}

			@container (max-width : 500px) {
				margin-top: var(--jw-spacing-2);
			}

			@container (max-width : 400px) {
				margin-top: var(--jw-spacing-2);
			}

			@container (max-width : 300px) {
				margin-top: var(--jw-spacing-1);
			}
		}
	}
}

//remove opacity on ios devices as it causes flickering
html.ios .sr__list:active {
	opacity: unset;
}
</style>
